/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useRef, ReactElement, Fragment } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getPatternModal } from '../../../style';

import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import TextArea from '../../../atoms/TextArea';
import { acceptOffer } from '../../../actions/profileTrackAction';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { IsMobileViewPort } from 'utils/helpers';
import { mobileContentStyle, mobileOverlayStyle } from 'components/common/CommonStyle';
import OfferModalFooter from './OfferModalFooter';
import breakpoints from 'theme/material/breakpoints';
import { isMobile } from 'react-device-detect';
import borders from 'theme/borders';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  gap: 20,
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
  })
);

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: '1',
  justifyContent: 'space-between',
});

const MessageTextArea = styled(TextArea)({
  border: '1px solid #C4C4C4 !important',
  boxShadow: 'inset 2px 2px 4px rgb(0 0 0 / 12%)',
  borderRadius: '6px !important',
  marginTop: 0,
  padding: '10px !important',
  overflow: 'unset !important',
});

const ButtonDiv = styled('div')({
  marginTop: 0,
});

interface OfferAcceptMessageProps {
  isModalOpen: boolean;
  offerAmount: any;
  avatarSrc: any;
  username: any;
  buttonID: any;
  closeModal: any;
  onSubmit: any;
  customContentStyle?: any;
}

const OfferAcceptMessage = ({
  isModalOpen,
  offerAmount,
  avatarSrc,
  username,
  buttonID,
  closeModal,
  onSubmit,
  customContentStyle,
}: OfferAcceptMessageProps): ReactElement => {
  const [dealMessage, setDealMessage] = useState('');
  const [isAcceptingOffer, setIsAcceptingOffer] = useState(false);
  const unlockCancelBtn = useRef(null);
  const { showMandatoryWall } = useMandatoryWall();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsAcceptingOffer(true);
    showMandatoryWall(
      () => {
        onSubmit(dealMessage)
          .then(() => {
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            acceptOffer(e);
            closeModal();
            setIsAcceptingOffer(false);
          })
          .catch(() => {
            setIsAcceptingOffer(false);
          });
      },
      () => setIsAcceptingOffer(false),
      'Accept Offer'
    );
  };

  const getHeadline = () => {
    return (
      <div>
        <div>
          You&lsquo;re about to accept <strong>{username}</strong>&lsquo;s offer of {offerAmount}.
        </div>
        <div style={{ marginTop: 10 }}>Send an intro message and start planning your first date!</div>
      </div>
    );
  };

  return (
    <ModalWithPattern
      isModalOpen={isModalOpen}
      dataTestID="offer-accept-message"
      closeBtnClassName="offer-accept-message-modal-close"
      closeBtnDataTestId="offer-accept-message-modal-close"
      modalContentLabel="Offer Accept and Message"
      onCloseClick={closeModal}
      closeRequest={closeModal}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
      withoutBgPattern={false}
      hideCloseBtn={false}
      closeOnOverlayClick={true}
      enableTouchMove={true}
    >
      <Fragment>
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={'100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                  border: `1px solid ${borders.light.primary}`,
                }}
              />
              <TitleDiv>Accept Offer?</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                margin: '20px auto',
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.3px',
                width: isMobile ? '70%' : 'auto',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline()}
            </Headline>
          </HeaderDiv>
          <Content>
            <MessageTextArea
              id="dealMessage"
              rows={4}
              onChange={e => setDealMessage(e.target.value)}
              value={dealMessage}
              placeholder={`Type your message to ${username} here...`}
              customInputStyle={{ fontSize: '16px' }}
              data-test-id="first-message-input"
            />
            <ButtonDiv>
              <ButtonSet align="center">
                <Button
                  id={buttonID}
                  buttonType="error"
                  onClick={handleSubmit}
                  type="submit"
                  data-test-id="accept-sent-offer-btn"
                  customStyle={{
                    fontSize: 18,
                    lineHeight: '16px',
                    letterSpacing: -0.3,
                    padding: '12px 28px',
                    borderRadius: '4px !important',
                  }}
                >
                  {isAcceptingOffer ? 'Accepting offer' : 'Accept & Send Message'}
                </Button>
              </ButtonSet>
              <ButtonSet align="center">
                <Button
                  buttonType="chromeless"
                  buttonRef={unlockCancelBtn}
                  data-test-id={`${buttonID}Cancel`}
                  onClick={e => {
                    e.preventDefault();
                    closeModal();
                  }}
                  customStyle={{
                    color: getPatternModal().linkGrey,
                    fontWeight: 'normal',
                    fontSize: 15,
                    lineHeight: '17px',
                    height: 'auto',
                    padding: '20px 0',
                  }}
                >
                  {' '}
                  Cancel
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
      </Fragment>
      <OfferModalFooter />
    </ModalWithPattern>
  );
};

export default React.memo(OfferAcceptMessage);
